import type { ReactNode } from "react";
import { Link } from "react-router";
import { Alerta, Badge, BotonLink, BotonWhatsapp, Button, Casilla, Combobox, DataList, Dialog, Row, Select, Spinner, Stack, TextArea, TextField } from "@/components";
import type { Proveedor } from "@/lib/api";
import { formatCuit } from "@/lib/fideicomisos";
import { ETIQUETA_IVA, OPCIONES_IVA, SIN_VALOR } from "@/lib/proveedores";
import { formatTelefono, linkTelefono } from "@/lib/telefonos";
import { useProveedoresTab } from "./useProveedoresTab";

const OPCIONES_IVA_FORM = [{ valor: SIN_VALOR, etiqueta: "Sin especificar" }, ...OPCIONES_IVA];

const guion = (v: string | null) => v ?? "—";
const CLASE_LINK = "text-primario underline-offset-2 hover:underline";

/** Con `onTocar`, cada etiqueta es un botón (en la lista: filtra por ella). */
function Etiquetas({ nombres, onTocar }: { nombres: string[]; onTocar?: (nombre: string) => void }) {
  if (nombres.length === 0) return <>—</>;
  return (
    <Row envolver espacio={1}>
      {nombres.map((e) => (
        <Badge key={e} onTocar={onTocar && (() => onTocar(e))} etiquetaTocar={`Ver todos los de ${e}`}>
          {e}
        </Badge>
      ))}
    </Row>
  );
}

function Telefono({ tel, nombre, onWhatsapp }: { tel: string | null; nombre: string; onWhatsapp?: () => void }) {
  const llamar = linkTelefono(tel);
  return (
    <Row envolver espacio={3}>
      <span>{formatTelefono(tel)}</span>
      {llamar && (
        <a href={llamar} className={CLASE_LINK}>
          Llamar
        </a>
      )}
      <BotonWhatsapp telefono={tel} nombre={nombre} onTocar={onWhatsapp} />
    </Row>
  );
}

function Ficha({ p, onWhatsapp }: { p: Proveedor; onWhatsapp?: () => void }) {
  const datos: [string, ReactNode][] = [
    ["Etiquetas", <Etiquetas nombres={p.etiquetas} />],
    ["CUIT", p.cuit ? formatCuit(p.cuit) : "—"],
    ["Condición frente al IVA", p.condicion_iva ? ETIQUETA_IVA[p.condicion_iva] : "—"],
    ["Contacto", guion(p.contacto)],
    ["Teléfono", <Telefono tel={p.telefono} nombre={p.nombre} onWhatsapp={onWhatsapp} />],
    ["Email", guion(p.email)],
    ["Banco", guion(p.banco)],
    ["CBU o alias", guion(p.cbu_alias)],
    ["Notas", guion(p.notas)],
    ["De parte de", guion(p.recomendado_por)],
    ["Validado", p.validado ? "Sí, ya trabajamos y damos fe" : "No"],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
      {datos.map(([etiqueta, valor]) => (
        <div key={etiqueta} className="contents">
          <dt className="text-texto-suave">{etiqueta}</dt>
          <dd className="break-words">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ProveedoresTab() {
  const t = useProveedoresTab();
  const d = t.dialogo;

  // Validado y Archivado van al lado del nombre: sin columna Estado (Mati, 2026-10-19).
  const nombre = (p: Proveedor) => (
    <Row envolver espacio={2}>
      <span>{p.nombre}</span>
      {p.validado && <Badge tono="exito">Validado</Badge>}
      {!p.activo && <Badge>Archivado</Badge>}
    </Row>
  );
  const columnas = [
    { clave: "nombre", titulo: "Nombre", render: nombre },
    { clave: "etiquetas", titulo: "Etiquetas", render: (p: Proveedor) => <Etiquetas nombres={p.etiquetas} onTocar={t.filtrarPorEtiqueta} /> },
    { clave: "telefono", titulo: "Teléfono", render: (p: Proveedor) => formatTelefono(p.telefono) },
  ];

  // Con mensaje armado (Configuración → Mensajes a proveedores), el ícono abre el mensaje para revisarlo antes de mandarlo.
  const alWhatsapp = (p: Proveedor) => (t.tieneMensaje(p) ? () => t.abrirMensaje(p) : undefined);
  const whatsapp = (p: Proveedor) => <BotonWhatsapp telefono={p.telefono} nombre={p.nombre} onTocar={alWhatsapp(p)} />;

  const acciones = (p: Proveedor) => {
    if (!t.esAdmin) {
      return (
        <>
          <Button variante="secundario" tamanio="sm" aria-label={`Ver ficha de ${p.nombre}`} onClick={() => t.abrirVer(p)}>
            Ver
          </Button>
          {whatsapp(p)}
        </>
      );
    }
    return (
      <>
        <Button variante="secundario" tamanio="sm" aria-label={`Ver ficha de ${p.nombre}`} onClick={() => t.abrirVer(p)}>
          Ver
        </Button>
        <Button variante="secundario" tamanio="sm" aria-label={`Editar ${p.nombre}`} onClick={() => t.abrirEditar(p)}>
          Editar
        </Button>
        <Button variante="secundario" tamanio="sm" aria-label={`${p.activo ? "Archivar" : "Reactivar"} ${p.nombre}`} onClick={() => void t.cambiarEstado(p)}>
          {p.activo ? "Archivar" : "Reactivar"}
        </Button>
        <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${p.nombre}`} onClick={() => t.abrirBorrar(p)}>
          Borrar
        </Button>
        {whatsapp(p)}
      </>
    );
  };

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Agenda</h1>
        {t.esAdmin && <Button onClick={t.abrirNuevo}>Nuevo proveedor</Button>}
      </Row>
      {t.esAdmin && (
        <p className="-mt-3 text-chico text-texto-suave">La agenda de proveedores es compartida entre todos los desarrollos: lo que cargues o edites acá se ve en todos.</p>
      )}
      <Row envolver espacio={3}>
        <TextField
          etiqueta="Buscar"
          placeholder="Nombre, CUIT, etiqueta o teléfono"
          value={t.texto}
          onChange={(e) => t.setTexto(e.target.value)}
          contenedorClassName="min-w-56 flex-1"
        />
        <Select etiqueta="Etiqueta" opciones={t.opcionesFiltro} valor={t.etiquetaFiltro} onCambio={t.setEtiquetaFiltro} className="min-w-56" />
        <div className="self-end">
          <Button variante="secundario" aria-pressed={t.archivados} onClick={t.alternarArchivados}>
            {t.archivados ? "Ocultar archivados" : "Ver archivados"}
          </Button>
        </div>
      </Row>
      {t.aviso && (
        <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
          {t.aviso}
        </p>
      )}
      <Alerta texto={t.errorEstado} />
      {t.errorEtiquetas && (
        <Stack espacio={3}>
          <Alerta texto={t.errorEtiquetas} />
          <div>
            <Button variante="secundario" onClick={t.reintentarEtiquetas}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList
          etiqueta="Proveedores"
          columnas={columnas}
          filas={t.proveedores}
          claveFila={(p) => p.id}
          acciones={acciones}
          vacio="No hay proveedores para mostrar."
        />
      )}

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "form" && d.proveedor ? "Editar proveedor" : "Nuevo proveedor"}>
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Nombre o razón social" value={t.form.nombre} onChange={(e) => t.cambiar("nombre", e.target.value)} error={t.errores.nombre} />
            <TextField etiqueta="CUIT" inputMode="numeric" value={t.form.cuit} onChange={(e) => t.cambiar("cuit", e.target.value)} error={t.errores.cuit} />
            <Select
              etiqueta="Condición frente al IVA"
              opciones={OPCIONES_IVA_FORM}
              valor={t.form.condicion_iva || undefined}
              onCambio={(v) => t.cambiar("condicion_iva", v)}
              placeholder="Sin especificar"
            />
            <Stack espacio={2}>
              <Combobox
                etiqueta="Etiquetas"
                opciones={t.opcionesEtiquetaForm}
                onElegir={t.agregarEtiqueta}
                onCrear={t.agregarEtiqueta}
                placeholder="Ej.: demolición, estudio de suelos"
              />
              {t.opcionesEtiquetaForm.length > 0 && (
                <div role="group" aria-label="Etiquetas ya creadas">
                  <p className="mb-1 text-chico text-texto-suave">Ya creadas (tocá para agregar):</p>
                  <Row envolver espacio={1}>
                    {t.opcionesEtiquetaForm.map((o) => (
                      <Badge key={o.valor} onTocar={() => t.agregarEtiqueta(o.valor)} etiquetaTocar={`Agregar etiqueta ${o.etiqueta}`}>
                        + {o.etiqueta}
                      </Badge>
                    ))}
                  </Row>
                </div>
              )}
              {t.form.etiquetas.length > 0 && (
                <Row envolver espacio={2}>
                  {t.form.etiquetas.map((e) => (
                    <Badge key={e} tono="primario" onQuitar={() => t.quitarEtiqueta(e)} etiquetaQuitar={`Quitar etiqueta ${e}`}>
                      {e}
                    </Badge>
                  ))}
                </Row>
              )}
              <Alerta texto={t.errorEtiqueta} />
            </Stack>
            <TextField etiqueta="Contacto" ayuda="Nombre de la persona" value={t.form.contacto} onChange={(e) => t.cambiar("contacto", e.target.value)} error={t.errores.contacto} />
            <TextField etiqueta="Teléfono" type="tel" ayuda="Con característica, ej. 11 5555-5555: se guarda en un solo formato para WhatsApp" value={t.form.telefono} onChange={(e) => t.cambiar("telefono", e.target.value)} error={t.errores.telefono} />
            <TextField etiqueta="Email" type="email" inputMode="email" value={t.form.email} onChange={(e) => t.cambiar("email", e.target.value)} error={t.errores.email} />
            <TextField etiqueta="Banco" value={t.form.banco} onChange={(e) => t.cambiar("banco", e.target.value)} error={t.errores.banco} />
            <TextField etiqueta="CBU o alias" value={t.form.cbu_alias} onChange={(e) => t.cambiar("cbu_alias", e.target.value)} error={t.errores.cbu_alias} />
            <TextField etiqueta="Notas" value={t.form.notas} onChange={(e) => t.cambiar("notas", e.target.value)} error={t.errores.notas} />
            <TextField
              etiqueta="De parte de"
              ayuda="Quién lo recomendó, para hablarle de su parte. Ej.: Javier"
              value={t.form.recomendado_por}
              onChange={(e) => t.cambiar("recomendado_por", e.target.value)}
              error={t.errores.recomendado_por}
            />
            <Casilla etiqueta="Validado" ayuda="Ya trabajamos con él y damos fe de que trabaja bien." marcado={t.form.validado} onCambio={t.cambiarValidado} />
            <p className="text-chico text-texto-suave">Los fiduciantes ven sólo el nombre y las etiquetas. El resto, sólo admin y contador.</p>
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "ver"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "ver" ? d.proveedor.nombre : ""}>
        {d?.tipo === "ver" && (
          <Stack espacio={4}>
            <Ficha p={d.proveedor} onWhatsapp={alWhatsapp(d.proveedor)} />
            <Link to={`/cotizaciones?proveedor=${d.proveedor.id}`} className={CLASE_LINK}>
              Ver presupuestos
            </Link>
            <Button onClick={t.cerrar}>Cerrar</Button>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar proveedor">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>¿Borrar a {d.proveedor.nombre}? Si ya tiene gastos cargados, en vez de borrarse queda archivado.</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
      <Dialog abierto={d?.tipo === "mensaje"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "mensaje" ? `Mensaje para ${d.proveedor.nombre}` : ""} ancho="amplio">
        {d?.tipo === "mensaje" && (
          <Stack espacio={4}>
            {t.opcionesMensaje.length > 1 && <Select etiqueta="Mensaje" opciones={t.opcionesMensaje} valor={t.mensajeElegido} onCambio={t.elegirMensaje} />}
            <TextArea etiqueta="Texto" value={t.textoMensaje} filas={8} onChange={(e) => t.setTextoMensaje(e.target.value)} ayuda="Podés retocarlo antes de mandarlo: no cambia el mensaje guardado." />
            {t.avisosMensaje && t.avisosMensaje.vacias.length > 0 && (
              <Alerta
                tono="alerta"
                texto={`Sin valor todavía: ${t.avisosMensaje.vacias.join(", ")}. Cargalo en Configuración → Mensajes a proveedores o completalo acá.`}
              />
            )}
            <Row justificar="fin" envolver espacio={2}>
              {t.esAdmin && (
                <Link to="/configuracion/mensajes" className={`${CLASE_LINK} self-center text-chico`}>
                  Editar los mensajes
                </Link>
              )}
              <Button variante="secundario" onClick={() => void t.copiarMensaje()}>
                {t.copiado ? "Copiado" : "Copiar"}
              </Button>
              {t.linkMensaje && (
                <BotonLink href={t.linkMensaje} externo>
                  Abrir WhatsApp
                </BotonLink>
              )}
            </Row>
            <span aria-live="polite" className="sr-only">
              {t.copiado ? "Copiado al portapapeles" : ""}
            </span>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
